import React, { useCallback, useContext, useEffect, useMemo } from "react";
import { PoweroffOutlined } from "@ant-design/icons";
import { getQsParam } from "../../../src/utils/string";
import { AuthContext } from "../../constants/contexts";
import usePersistedState from "../../hooks/usePersistedState";
import { parseToken } from "./utils";
import { API_BASE_URL, IS_WHITE_LABEL } from "../../constants";
import useResponsiveModal from "../../hooks/useResponsiveModal";
import { JsonStorage } from "../../utils/storage";

async function revokeServerSession(token) {
  if (!token) return;
  try {
    const base = API_BASE_URL.replace(/\/$/, "");
    await fetch(`${base}/auth/logout`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch (e) {
    // best-effort: if the server is unreachable, still clear local state
  }
}

function AuthProvider({ children }) {
  const [user, setUser] = usePersistedState(null, "auth-provider");
  const { confirm, contextHolder } = useResponsiveModal();

  const clearSession = useCallback(async () => {
    await revokeServerSession(user?.token);
    setUser(null);
  }, [user, setUser]);

  const handleLogout = useCallback(() => {
    confirm({
      title: "خروج از حساب",
      content: "از حساب خود خارج می‌شوید؟",
      icon: <PoweroffOutlined />,
      onOk: () => {
        clearSession();
      },
      okText: "بله",
      cancelText: "خیر",
    });
  }, [confirm, clearSession]);
  const handleToken = useCallback((token) => {
    const parsed = parseToken(token);

    setUser({ token, ...parsed });
    setTimeout(()=>window.location.reload(),500);
  }, []);

  const contextValue = useMemo(
    () => ({
      user: isTokenValid(user) ? user:null,
      handleToken,
      handleLogout,
      logout: clearSession,
      isLoggedIn: Boolean(user),
    }),
    [user, handleToken, handleLogout, clearSession]
  );

  useEffect(() => {
    const token = getQsParam("auth_token");
    if (!token) {
      return;
    }
    
    const parsed = parseToken(token);
    if (!parsed || parsed.exp * 1000 < Date.now()) {
      return;
    }
    setUser({ token, ...parsed });

    const redirect = sessionStorage.getItem('post_login_redirect') || (IS_WHITE_LABEL ? '/finance' : '/marketplace');
    sessionStorage.removeItem('post_login_redirect');
    setTimeout(() => window.location.replace(redirect));
  }, []);

  return (
    <AuthContext.Provider value={contextValue} displayName="AuthProvider">
      {children}
      {contextHolder}
    </AuthContext.Provider>
  );
}

AuthProvider.AUTH_KEY = 'auth-provider';
AuthProvider.getUser = () => {
  const user = JsonStorage.get(AuthProvider.AUTH_KEY);
  if (!isTokenValid(user)) {
    return null;
  }
  return user;
};
AuthProvider.useAuth = () => {
  return useContext(AuthContext);
}
function isTokenValid(user) {
  return user && user.exp && user.exp * 1000 > Date.now();
}
export default AuthProvider;
